import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./../../styleSheet/main/admin/addAnnouncements/addAnnouncements.css";
import orangeSortIcon from "./../../media/orange-sort-icon.svg";
import { useNavigate, useParams } from "react-router-dom";
import carIcon from "./../../media/announcements/car.svg";
import computerIcon from "./../../media/announcements/computer.svg";
import documentsIcon from "./../../media/announcements/documents.svg";
import exclamationIcon from "./../../media/announcements/exclamation.svg";
import familyIcon from "./../../media/announcements/family.svg";
import planeIcon from "./../../media/announcements/plane.svg";
import foodIcon from "./../../media/announcements/food.svg";
import plantIcon from "./../../media/announcements/plant.svg";
import questionIcon from "./../../media/announcements/question.svg";
import starIcon from "./../../media/announcements/star.svg";
import toolsIcon from "./../../media/announcements/tools.svg";
import peopleIcon from "./../../media/announcements/people.svg";
import Logger from "../../utils/Logger";
import { useNotification } from "../../components/NotificationContext";
import httpClient from "../../services/httpClient";
import RichTextEditor from "../../components/richTextEditor/richTextEditor";
import { LoadingComponent } from "../../App";

function EditAnnouncement() {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [hover, setHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isIconListOpen, setIsIconListOpen] = useState(false);
  // const { showNotification } = useNotification();

  const handleIconSelect = (iconKey) => {
    setSelectedIcon(iconKey);
    setIsIconListOpen(false); // Zamknięcie listy po wyborze ikony
  };

  const ICONS = {
    car: carIcon,
    computer: computerIcon,
    documents: documentsIcon,
    exclamation: exclamationIcon,
    family: familyIcon,
    plane: planeIcon,
    food: foodIcon,
    plant: plantIcon,
    question: questionIcon,
    star: starIcon,
    tools: toolsIcon,
    people: peopleIcon,
  };

  const [selectedIcon, setSelectedIcon] = useState("exclamation"); // Domyślna ikona

  // Fetch existing announcement data
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        setIsLoadingData(true);
        const response = await httpClient.get(`/announcements/${id}`, {
          withAuth: true,
        });

        const announcementData = response.data;
        setTitle(announcementData.title || "");
        setDetails(announcementData.details || "");
        setSelectedIcon(announcementData.icon || "exclamation");

        Logger.log("Loaded announcement data:", announcementData);
      } catch (error) {
        Logger.error("Error fetching announcement:", error);
        // showNotification("Nie udało się załadować ogłoszenia.", "error");
        navigate("/administrator/aktualnosci");
      } finally {
        setIsLoadingData(false);
      }
    };

    if (id) {
      fetchAnnouncement();
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Walidacja
    if (!title || !details) {
      // showNotification("Wszystkie pola są wymagane!", "error");
      return;
    }

    setIsLoading(true);

    const announcementData = {
      title: title,
      details: details,
      icon: selectedIcon,
    };

    try {
      const response = await httpClient.put(
        `/announcements/${id}`,
        announcementData,
        {
          withAuth: true,
        },
      );

      const result = response.data;
      Logger.log("Odpowiedź serwera:", result);

      setSuccessMessage("Ogłoszenie zostało zaktualizowane pomyślnie!");
      // showNotification(
      //   "Ogłoszenie zostało zaktualizowane pomyślnie!",
      //   "success"
      // );

      setTimeout(() => {
        navigate("/administrator/aktualnosci");
      }, 500);
    } catch (error) {
      Logger.error("Błąd:", error);
      // showNotification(
      //   "Nie udało się zaktualizować ogłoszenia. Spróbuj ponownie.",
      //   "error"
      // );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return <LoadingComponent />;
  }

  return (
    <div className="add-announcement-container">
      <div className="main-site">
        <h1>{t("announcementEditor.editTitle")}</h1>
        <div
          className="orange-sort-icon"
          onClick={() => navigate("/administrator/aktualnosci")}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <form onSubmit={handleSubmit} className="add-announcement-form">
        <div className="form-layout">
          {/* LEWA STRONA - WYBÓR IKONY */}
          <div className="icon-section">
            <label htmlFor="icon">{t("announcementEditor.selectIcon")}</label>
            <div className="icon-selector">
              <img
                src={ICONS[selectedIcon]}
                alt="Wybrana ikona"
                className="selected-icon"
                onClick={() => setIsIconListOpen(!isIconListOpen)}
              />
              {isIconListOpen && (
                <div className="icon-list">
                  {Object.keys(ICONS).map((iconKey) => (
                    <div key={iconKey} className="icon-item-wrapper">
                      <img
                        src={ICONS[iconKey]}
                        alt={iconKey}
                        className="icon-item"
                        onClick={() => handleIconSelect(iconKey)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* PRAWA STRONA - TYTUŁ I SZCZEGÓŁY */}
          <div className="details-section">
            <div className="form-group">
              <label htmlFor="title">
                {t("announcementEditor.announcementTitle")}
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("announcementEditor.enterTitle")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="details">
                {t("announcementEditor.announcementDetails")}
              </label>
              <RichTextEditor content={details} onChange={setDetails} />
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading
            ? t("announcementEditor.updating")
            : t("announcementEditor.updateButton")}
        </button>
      </form>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
    </div>
  );
}

export default EditAnnouncement;
